import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'
import { MOMENT, ease, envelope, progress } from '@/components/landing/film/timeline'
import { FINISHES } from '@/constants/finishes'

import {
    fixed,
    setLayer,
    setStyle
} from '@/components/landing/film/overlay-utils'

interface SceneFinishesProps {
    register: RegisterUpdater
}

const WIDE: [number, number] = [14.3, 15.02]
const BADGE: [number, number] = [14.4, 18.95]

// Scene 04: the three finishes as a kinetic triptych, then each one up close
const SceneFinishes: FC<SceneFinishesProps> = ({ register }): ReactNode => {
    const layerRef = useRef<HTMLDivElement>(null)
    const labelRefs = useRef<Array<HTMLDivElement | null>>([])
    const dividerRefs = useRef<Array<HTMLDivElement | null>>([])
    const cardRefs = useRef<Array<HTMLDivElement | null>>([])
    const swatchRefs = useRef<Array<HTMLDivElement | null>>([])
    const badgeRef = useRef<HTMLDivElement>(null)
    const badgeRingRef = useRef<SVGGElement>(null)

    useEffect(() => register((t, real, frame) => {
        const active = t >= 14.2 && t < 19.05
        setLayer(layerRef.current, active ? 1 : 0)
        if (!active) return

        const wide = envelope(t, WIDE[0], WIDE[1], 0.25, 0.15)
        frame.triptych.forEach((wheel, i) => {
            const label = labelRefs.current[i]
            const enter = ease.outExpo(progress(t, WIDE[0] + 0.08 + i * 0.08, WIDE[0] + 0.6 + i * 0.08))
            setStyle(label, 'left', `${fixed(wheel.x, 1)}px`)
            setStyle(label, 'top', `${fixed(wheel.y + wheel.r * 1.1, 1)}px`)
            setStyle(label, 'opacity', fixed(wide * enter, 3))
            setStyle(label, 'transform', `translate(-50%, ${fixed((1 - enter) * 12, 2)}px)`)
        })

        dividerRefs.current.forEach((divider, i) => {
            const a = frame.triptych[i]
            const b = frame.triptych[i + 1]
            const grow = ease.outExpo(progress(t, WIDE[0], WIDE[0] + 0.5))
            if (frame.portrait) {
                setStyle(divider, 'left', '12%')
                setStyle(divider, 'width', '76%')
                setStyle(divider, 'height', '1px')
                setStyle(divider, 'top', `${fixed((a.y + b.y) / 2, 1)}px`)
                setStyle(divider, 'transform', `scaleX(${fixed(grow, 3)})`)
                setStyle(divider, 'background', 'linear-gradient(90deg, transparent, rgb(255 215 0 / 0.6), transparent)')
            }
            else {
                setStyle(divider, 'top', '14%')
                setStyle(divider, 'height', '72%')
                setStyle(divider, 'width', '1px')
                setStyle(divider, 'left', `${fixed((a.x + b.x) / 2, 1)}px`)
                setStyle(divider, 'transform', `scaleY(${fixed(grow, 3)})`)
                setStyle(divider, 'background', 'linear-gradient(180deg, transparent, rgb(255 215 0 / 0.6), transparent)')
            }
            setStyle(divider, 'opacity', fixed(wide * 0.7, 3))
        })

        FINISHES.forEach((_, i) => {
            const start = MOMENT.finishFocus[i]
            const end = MOMENT.finishEnd[i]
            const enter = ease.outExpo(progress(t, start + 0.05, start + 0.7))
            const leave = progress(t, end - 0.22, end)
            const card = cardRefs.current[i]
            setStyle(card, 'opacity', fixed(enter * (1 - leave), 3))
            setStyle(card, 'transform', `translateY(${fixed((1 - enter) * 26 - leave * 10, 2)}px)`)
            setStyle(card, 'clip-path', `inset(0 0 ${fixed((1 - enter) * 100, 2)}% 0)`)
            setStyle(swatchRefs.current[i], 'transform', `scaleX(${fixed(ease.outCubic(progress(t, start + 0.25, start + 0.9)), 3)})`)
        })

        const badge = envelope(t, BADGE[0], BADGE[1], 0.5, 0.3)
        const pop = ease.outBack(progress(t, BADGE[0], BADGE[0] + 0.7))
        setStyle(badgeRef.current, 'opacity', fixed(badge, 3))
        setStyle(
            badgeRef.current,
            'transform',
            `perspective(700px) translateY(${fixed(Math.sin(real * 1.3) * 6, 2)}px) rotateX(${fixed(Math.sin(real * 0.9) * 12, 2)}deg) rotateY(${fixed(Math.cos(real * 0.7) * 16, 2)}deg) scale(${fixed(0.6 + pop * 0.4, 3)})`
        )
        setStyle(badgeRingRef.current, 'transform', `rotate(${fixed(real * 22, 2)}deg)`)
    }), [register])

    return (
        <div ref={layerRef} className='pointer-events-none absolute inset-0' style={{ opacity: 0, visibility: 'hidden' }}>
            {[0, 1].map((i) => (
                <div
                    key={i}
                    ref={(element) => {
                        dividerRefs.current[i] = element
                    }}
                    className='absolute'
                    style={{ opacity: 0 }}
                />
            ))}

            {FINISHES.map((finish, i) => (
                <div
                    key={finish.key}
                    ref={(element) => {
                        labelRefs.current[i] = element
                    }}
                    className='absolute whitespace-nowrap text-center'
                    style={{ opacity: 0 }}
                >
                    <span className='film-mono text-gold'>
                        {finish.number}
                    </span>
                    <span className='film-mono ml-2 text-titanium-light'>
                        {finish.name}
                    </span>
                </div>
            ))}

            {FINISHES.map((finish, i) => (
                <div
                    key={finish.key}
                    ref={(element) => {
                        cardRefs.current[i] = element
                    }}
                    className='film-finish-card absolute'
                    style={{ opacity: 0 }}
                >
                    <p className='film-mono text-titanium-light'>
                        <span className='text-gold'>{finish.number}</span> / 03 &middot; Finish
                    </p>
                    <p className='film-heading mt-[1.2cqh] text-white'>
                        {finish.name}
                    </p>
                    <div
                        ref={(element) => {
                            swatchRefs.current[i] = element
                        }}
                        className='mt-[1.6cqh] h-1.5 w-full origin-left rounded-full'
                        style={{ background: finish.swatch, transform: 'scaleX(0)' }}
                    />
                    <p className='film-body mt-[1.6cqh] text-titanium-light'>
                        {finish.copy}
                    </p>
                </div>
            ))}

            <div ref={badgeRef} className='film-badge absolute aspect-square' style={{ opacity: 0 }}>
                <svg viewBox='0 0 200 200' className='size-full overflow-visible' role='img' aria-label='100% tailor-made fitment'>
                    <defs>
                        <path id='film-badge-ring' d='M 100 100 m -76 0 a 76 76 0 1 1 152 0 a 76 76 0 1 1 -152 0' />
                        <linearGradient id='film-badge-gold' x1='0' y1='0' x2='1' y2='1'>
                            <stop offset='0' stopColor='#fff1a8' />
                            <stop offset='0.5' stopColor='#ffd700' />
                            <stop offset='1' stopColor='#a8801c' />
                        </linearGradient>
                    </defs>
                    <circle cx='100' cy='100' r='97' fill='rgba(10, 12, 16, 0.6)' stroke='url(#film-badge-gold)' strokeWidth='1.5' />
                    <circle cx='100' cy='100' r='58' fill='none' stroke='#ffd700' strokeOpacity='0.35' strokeWidth='1' />
                    <g ref={badgeRingRef} style={{ transformOrigin: '100px 100px', transformBox: 'view-box' }}>
                        <text fill='#ffd700' fontSize='13' letterSpacing='4.2' style={{ fontFamily: 'var(--font-mono)' }}>
                            <textPath href='#film-badge-ring'>
                                TAILOR-MADE FITMENT &#8226; TAILOR-MADE FITMENT &#8226;
                            </textPath>
                        </text>
                    </g>
                    <text x='100' y='106' textAnchor='middle' fill='url(#film-badge-gold)' fontSize='36' fontWeight='700' style={{ fontFamily: 'var(--font-heading)' }}>
                        100%
                    </text>
                    <text x='100' y='128' textAnchor='middle' fill='#c4ccd6' fontSize='9' letterSpacing='3' style={{ fontFamily: 'var(--font-mono)' }}>
                        BESPOKE
                    </text>
                </svg>
            </div>
        </div>
    )
}

export default SceneFinishes
